import React, { useState } from 'react';
import { motion } from 'framer-motion';

interface MovieSearchModalProps {
  onClose: () => void;
  onSelectMovie?: (movie: MovieResult) => void;
}

interface MovieResult {
  id: string;
  title: string;
  image?: string;
  year?: string;
  isSeries?: boolean;
  description?: string;
  contentRating?: string;
  seasonNumber?: number;
  episodeNumber?: number;
  episodeTitle?: string;
  subtitles?: string;
  selectedLanguage?: string;
  dictionaryEntries?: DictionaryEntry[];
}

interface DictionaryEntry {
  phrase: string;
  translation: string;
  definition: string;
  scene_description: string;
}

type ContentType = 'none' | 'movie' | 'series';

const MovieSearchModal: React.FC<MovieSearchModalProps> = ({ onClose, onSelectMovie }) => {
  const [contentType, setContentType] = useState<ContentType>('none');
  const [searchQuery, setSearchQuery] = useState('');
  const [seasonInput, setSeasonInput] = useState('');
  const [episodeInput, setEpisodeInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<MovieResult[]>([]);
  const [selectedMovie, setSelectedMovie] = useState<MovieResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSubtitles, setShowSubtitles] = useState(false);
  const [isLoadingSubtitles, setIsLoadingSubtitles] = useState(false);
  
  // New states for dictionary parameters
  const [showParameterSelection, setShowParameterSelection] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState('ru');
  const [cefrLevel, setCefrLevel] = useState('B1');
  const [speechStyle, setSpeechStyle] = useState('Colloquial');
  const [isGeneratingDictionary, setIsGeneratingDictionary] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState<string | null>(null);
  const [showDictionary, setShowDictionary] = useState(false);

  // Speech style descriptions
  const speechStyleDescriptions: Record<string, string> = {
    'Разговорный': 'Повседневная, неформальная речь; часто используется в непринужденных разговорах и диалогах. Примеры: "Как дела?" / "Да ладно!" / "Ты шутишь!"',
    'Литературный': 'Возвышенный, художественный язык; часто встречается в литературе, поэзии и драматических диалогах. Примеры: "Так разворачивается повествование." / "Тень отчаяния нависла над ним."',
    'Технический': 'Точный, предметно-специфический язык; используется в профессиональной и академической среде. Примеры: "Квантовая запутанность описывает явление, при котором..."',
    'Формальный': 'Изысканный, структурированный язык, используемый в официальном общении и выступлениях. Примеры: "Имеем честь пригласить вас на мероприятие."',
    'Слэнг': 'Неформальный, модный язык, часто специфичный для определенной субкультуры или поколения. Примеры: "Это огонь!" / "Она меня прокинула."',
    'Бизнес': 'Профессиональный, но доступный язык, используемый в корпоративной среде. Примеры: "Давайте согласуем ключевые результаты до конца дня."',
    'Юридический': 'Сложный, точный язык, используемый в законах, контрактах и официальных нормативных актах. Примеры: "В дальнейшем именуемый как сторона первого порядка..."',
    'Журналистский': 'Четкий, объективный язык, используемый в новостных статьях и репортажах. Примеры: "Официальные лица подтвердили инцидент сегодня утром."',
    'Академический': 'Структурированный, сложный язык, типичный для научных работ и университетских текстов. Примеры: "Данное исследование изучает корреляцию между..."',
    'Старомодный': 'Язык, звучащий устаревшим или встречающийся в исторических текстах. Примеры: "Воистину, ты мудр не по годам."',
    'Поэтический': 'Выразительный, метафоричный язык с ритмическими или символическими элементами. Примеры: "Волны шептали секреты берегу."',
    'Военный': 'Прямой, командный язык, используемый в военном контексте. Примеры: "Выдвигаемся в 0600 часов."',
    'Научный': 'Точная терминология, используемая в научном дискурсе и исследованиях. Примеры: "Митохондрия является энергетической фабрикой клетки."'
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      setError('Пожалуйста, введите название');
      return;
    }

    setIsSearching(true);
    setError(null);
    setSearchResults([]);
    setShowSubtitles(false);

    try {
      if (contentType === 'series' && seasonInput && episodeInput) {
        // Direct search for specific episode
        const seriesUrl = `https://www.omdbapi.com/?apikey=589623b7&t=${encodeURIComponent(searchQuery)}&type=series`;
        const seriesResponse = await fetch(seriesUrl);
        const seriesData = await seriesResponse.json();

        if (seriesData.Response === 'True') {
          const episodeUrl = `https://www.omdbapi.com/?apikey=589623b7&t=${encodeURIComponent(searchQuery)}&Season=${seasonInput}&Episode=${episodeInput}`;
          const episodeResponse = await fetch(episodeUrl);
          const episodeData = await episodeResponse.json();

          if (episodeData.Response === 'True') {
            const episode: MovieResult = {
              id: seriesData.imdbID,
              title: seriesData.Title,
              image: seriesData.Poster !== 'N/A' ? seriesData.Poster : '',
              year: seriesData.Year,
              isSeries: true,
              description: seriesData.Plot,
              seasonNumber: parseInt(seasonInput),
              episodeNumber: parseInt(episodeInput),
              episodeTitle: episodeData.Title
            };
            setSelectedMovie(episode);
          } else {
            setError('Эпизод не найден. Проверьте номер сезона и эпизода.');
          }
        } else {
          setError('Сериал не найден. Проверьте название.');
        }
      } else {
        // Regular movie search
        const url = `https://www.omdbapi.com/?apikey=589623b7&s=${encodeURIComponent(searchQuery)}${contentType === 'movie' ? '&type=movie' : ''}`;
        const response = await fetch(url);
        const data = await response.json();
        
        if (data.Response === 'True' && data.Search) {
          const movies = data.Search.map((movie: any) => ({
            id: movie.imdbID,
            title: movie.Title,
            image: movie.Poster !== 'N/A' ? movie.Poster : '',
            year: movie.Year,
            isSeries: movie.Type === 'series',
            description: movie.Type.charAt(0).toUpperCase() + movie.Type.slice(1)
          }));
          
          setSearchResults(movies);
        } else {
          setError(data.Error || 'Ничего не найдено. Попробуйте другой запрос.');
        }
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Произошла ошибка при поиске. Пожалуйста, попробуйте позже.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectMovie = (movie: MovieResult) => {
    setSelectedMovie({
      ...movie,
      selectedLanguage: 'ru'
    });
    setShowSubtitles(false);
  };

  const handleLanguageChange = (language: string) => {
    if (selectedMovie) {
      setSelectedMovie({
        ...selectedMovie,
        selectedLanguage: language
      });
    }
  };

  const fetchSubtitles = async (imdbId: string, language: string) => {
    setIsLoadingSubtitles(true);
    setError(null);
    
    try {
      const response = await fetch(
        `https://api.opensubtitles.com/api/v1/subtitles?imdb_id=${imdbId.replace('tt', '')}&languages=${language}`,
        {
          headers: {
            'Accept': '*/*',
            'Api-Key': 'uxTJorEb3ZqCG9n2Vd2BQlmBJV35cCAl',
            'Authorization': 'Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJRQUpKNkhoUTlDMzFka1RoYTJxM1BEWGFJZFozQWttMSIsImV4cCI6MTc0MTY0NTA2MX0.zU24A5ZNhXl6tfZn8yvKHr4p645Prt9rP2D3-lwbRO4',
            'User-Agent': 'LinguaApp v1.0',
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(`HTTP error! status: ${response.status}${errorData ? ` - ${JSON.stringify(errorData)}` : ''}`);
      }
      
      const data = await response.json();
      console.log('Subtitles API Response:', data);

      if (data?.data?.length > 0) {
        // We need to download the actual subtitle file
        const subtitleInfo = data.data[0];
        console.log('Selected subtitle:', subtitleInfo);

        // Make a second request to download the subtitle file
        const downloadResponse = await fetch(
          'https://api.opensubtitles.com/api/v1/download',
          {
            method: 'POST',
            headers: {
              'Accept': '*/*',
              'Api-Key': 'uxTJorEb3ZqCG9n2Vd2BQlmBJV35cCAl',
              'Authorization': 'Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJRQUpKNkhoUTlDMzFka1RoYTJxM1BEWGFJZFozQWttMSIsImV4cCI6MTc0MTY0NTA2MX0.zU24A5ZNhXl6tfZn8yvKHr4p645Prt9rP2D3-lwbRO4',
              'User-Agent': 'LinguaApp v1.0',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              file_id: subtitleInfo.attributes.files[0].file_id
            })
          }
        );

        if (!downloadResponse.ok) {
          throw new Error('Failed to download subtitle file');
        }

        const downloadData = await downloadResponse.json();
        console.log('Download response:', downloadData);

        if (downloadData.link) {
          // Fetch the actual subtitle content
          const subtitleResponse = await fetch(downloadData.link);
          const subtitleContent = await subtitleResponse.text();
          
          setSelectedMovie(prev => ({
            ...prev!,
            subtitles: subtitleContent
          }));
        } else {
          setError('Не удалось загрузить файл субтитров');
        }
      } else {
        setError('Субтитры не найдены для этого контента');
      }
    } catch (err) {
      console.error('Subtitle fetch error:', err);
      setError('Ошибка при загрузке субтитров. Пожалуйста, попробуйте позже.');
    } finally {
      setIsLoadingSubtitles(false);
    }
  };

  // Function to generate dictionary with GPT-4o-mini
  const generateDictionary = async () => {
    if (!selectedMovie || !selectedMovie.subtitles) {
      setError('Нет субтитров для создания словаря');
      return;
    }

    setIsGeneratingDictionary(true);
    setError(null);

    try {
      // Call our Firebase Function instead of OpenAI directly
      const response = await fetch(
        'https://us-central1-linguasub-f9ddf.cloudfunctions.net/generateDictionary',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            subtitles: selectedMovie.subtitles,
            subtitlesLanguage: selectedMovie.selectedLanguage || 'en',
            targetLanguage,
            cefrLevel,
            speechStyle
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Ошибка сервера' }));
        const errorMessage = errorData?.error || `Ошибка сервера: ${response.status}`;
        console.error('Dictionary generation failed:', errorMessage);
        throw new Error(`Error: ${response.status} - ${errorMessage}`);
      }

      const data = await response.json();
      console.log('Dictionary generation response:', data);

      if (data.entries && Array.isArray(data.entries)) {
        // The entries are nested one level deeper - need to check if they're in a nested array
        let entries = data.entries;
        
        // If the first item is an object with an entries array, use that instead
        if (entries.length === 1 && entries[0].entries && Array.isArray(entries[0].entries)) {
          entries = entries[0].entries;
        }
        
        // Verify that entries have the expected format
        if (entries.length === 0) {
          throw new Error('Не удалось создать словарь. Сервер вернул пустой список.');
        }
        
        // Validate that the entries have the required properties
        const validEntries = entries.filter((entry: any) => {
          return entry && 
                 typeof entry === 'object' && 
                 entry.phrase && 
                 entry.translation && 
                 entry.definition && 
                 entry.scene_description;
        });
        
        if (validEntries.length === 0) {
          throw new Error('Неверный формат полученных данных. Пожалуйста, попробуйте снова.');
        }
        
        setSelectedMovie(prev => ({
          ...prev!,
          dictionaryEntries: validEntries
        }));
        
        setShowDictionary(true);
      } else {
        throw new Error('Неверный формат ответа от сервиса создания словаря.');
      }
    } catch (err: any) {
      console.error('Dictionary generation error:', err);
      setError(`Ошибка при создании словаря: ${err.message || 'Пожалуйста, попробуйте позже.'}`);
    } finally {
      setIsGeneratingDictionary(false);
    }
  };

  const handleNextStep = () => {
    if (!selectedMovie) {
      return;
    }
    
    if (showDictionary) {
      // If dictionary is shown, pass the result to parent and close
      if (onSelectMovie) {
        // Pass the selected parameters along with the movie data
        const movieWithParams = {
          ...selectedMovie!,
          cefrLevel,
          speechStyle,
          targetLanguage
        };
        onSelectMovie(movieWithParams);
      }
      onClose();
      return;
    }
    
    if (showParameterSelection) {
      // User confirmed parameters, generate dictionary
      generateDictionary();
      return;
    }
    
    if (showSubtitles) {
      // Subtitles are shown, show parameters selection
      setShowParameterSelection(true);
      setShowSubtitles(false);
      return;
    }
    
    if (selectedMovie.subtitles) {
      // Already have subtitles, show parameters
      setShowParameterSelection(true);
    } else {
      // Get subtitles first
      fetchSubtitles(selectedMovie.id, selectedMovie.selectedLanguage || 'en');
      setShowSubtitles(true);
    }
  };

  // Render parameter selection UI
  const renderParameterSelection = () => (
    <div className="parameter-selection">
      <h3 className="results-title pencil-text">Настройте параметры словаря:</h3>
      
      <div className="parameter-group">
        <label className="parameter-label">Язык перевода:</label>
        <select 
          value={targetLanguage}
          onChange={(e) => setTargetLanguage(e.target.value)}
          className="parameter-select"
        >
          <option value="ru">Русский</option>
          <option value="en">English</option>
          <option value="es">Español</option>
          <option value="fr">Français</option>
          <option value="de">Deutsch</option>
        </select>
        <div className="parameter-description">
          Язык, на который будут переведены фразы из субтитров
        </div>
      </div>
      
      <div className="parameter-group">
        <label className="parameter-label">Уровень сложности (CEFR):</label>
        <select 
          value={cefrLevel}
          onChange={(e) => setCefrLevel(e.target.value)}
          className="parameter-select"
        >
          <option value="A1">A1 - Начальный</option>
          <option value="A2">A2 - Элементарный</option>
          <option value="B1">B1 - Средний</option>
          <option value="B2">B2 - Выше среднего</option>
          <option value="C1">C1 - Продвинутый</option>
          <option value="C2">C2 - Профессиональный</option>
        </select>
        <div className="parameter-description">
          Уровень сложности слов и фраз по Общеевропейской шкале
        </div>
      </div>
      
      <div className="parameter-group">
        <label className="parameter-label">Стиль речи:</label>
        <div className="speech-styles-list">
          {Object.keys(speechStyleDescriptions).map(style => (
            <div 
              key={style} 
              className={`speech-style-item ${speechStyle === style ? 'selected' : ''}`}
              onClick={() => setSpeechStyle(style)}
            >
              {style}
              <button 
                className="info-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInfoTooltip(showInfoTooltip === style ? null : style);
                }}
              >
                i
              </button>
              {showInfoTooltip === style && (
                <div className="info-tooltip">
                  {speechStyleDescriptions[style]}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Dictionary entry component
  const renderDictionary = () => {
    if (!selectedMovie?.dictionaryEntries) return null;
    
    return (
      <div className="dictionary-entries">
        <h3 className="results-title pencil-text">Ваш словарь:</h3>
        
        {selectedMovie.dictionaryEntries.map((entry, index) => (
          <div key={index} className="dictionary-entry">
            <div className="phrase">{entry.phrase}</div>
            <div className="translation">{entry.translation}</div>
            <div className="definition">{entry.definition}</div>
            <div className="scene">{entry.scene_description}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderContentTypeSelection = () => (
    <div className="content-type-selection">
      <h3 className="results-title pencil-text">Выберите тип контента:</h3>
      <div className="content-type-buttons">
        <button 
          className={`content-type-button ${contentType === 'movie' ? 'selected' : ''}`}
          onClick={() => setContentType('movie')}
        >
          Фильм
        </button>
        <button 
          className={`content-type-button ${contentType === 'series' ? 'selected' : ''}`}
          onClick={() => setContentType('series')}
        >
          Сериал
        </button>
      </div>
    </div>
  );

  const renderSeriesForm = () => (
    <form onSubmit={handleSearch} className="movie-search-form">
      <div className="series-form">
        <h3 className="results-title pencil-text">Найдите серию:</h3>
        <div className="series-inputs">
          <input
            type="text"
            className="movie-search-input"
            placeholder="Название сериала"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <input
            type="number"
            className="season-input"
            placeholder="Сезон"
            value={seasonInput}
            onChange={(e) => setSeasonInput(e.target.value)}
            min="1"
          />
          <input
            type="number"
            className="episode-input"
            placeholder="Эпизод"
            value={episodeInput}
            onChange={(e) => setEpisodeInput(e.target.value)}
            min="1"
          />
        </div>
        <button 
          type="submit" 
          className="search-button pencil-outline"
          disabled={isSearching}
        >
          {isSearching ? 'Поиск...' : 'Найти'}
        </button>
      </div>
      
      {error && <p className="search-error pencil-text">{error}</p>}
      
      {searchResults.length > 0 && (
        <div className="search-results">
          <h3 className="results-title pencil-text">Результаты поиска:</h3>
          <div className="movie-results-list">
            {searchResults.map((movie) => (
              <div 
                key={movie.id} 
                className={`movie-result-item ${selectedMovie?.id === movie.id ? 'selected' : ''}`}
                onClick={() => handleSelectMovie(movie)}
              >
                <div className="movie-poster">
                  {movie.image ? (
                    <img src={movie.image} alt={movie.title} />
                  ) : (
                    <div className="default-poster">
                      <span>🎬</span>
                    </div>
                  )}
                </div>
                <div className="movie-info">
                  <h4 className="movie-title pencil-text">{movie.title}</h4>
                  <div className="movie-details">
                    {movie.year && <span className="movie-year pencil-text">{movie.year}</span>}
                    {movie.seasonNumber && (
                      <span className="episode-info pencil-text">S{movie.seasonNumber}</span>
                    )}
                    {movie.episodeNumber && (
                      <span className="episode-info pencil-text">E{movie.episodeNumber}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </form>
  );

  const renderMovieForm = () => (
    <form onSubmit={handleSearch} className="movie-search-form">
      <div className="search-input-container">
        <input
          type="text"
          className="movie-search-input"
          placeholder="Введите название фильма"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button 
          type="submit" 
          className="search-button pencil-outline"
          disabled={isSearching}
        >
          {isSearching ? 'Поиск...' : 'Найти'}
        </button>
      </div>
      
      {error && <p className="search-error pencil-text">{error}</p>}
      
      {searchResults.length > 0 && (
        <div className="search-results">
          <h3 className="results-title pencil-text">Результаты поиска:</h3>
          <div className="movie-results-list">
            {searchResults.map((movie) => (
              <div 
                key={movie.id} 
                className={`movie-result-item ${selectedMovie?.id === movie.id ? 'selected' : ''}`}
                onClick={() => handleSelectMovie(movie)}
              >
                <div className="movie-poster">
                  {movie.image ? (
                    <img src={movie.image} alt={movie.title} />
                  ) : (
                    <div className="default-poster">
                      <span>🎬</span>
                    </div>
                  )}
                </div>
                <div className="movie-info">
                  <h4 className="movie-title pencil-text">{movie.title}</h4>
                  <div className="movie-details">
                    {movie.year && <span className="movie-year pencil-text">{movie.year}</span>}
                    {movie.isSeries && <span className="movie-type pencil-text">Сериал</span>}
                  </div>
                  {movie.description && (
                    <p className="movie-description pencil-text">{movie.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </form>
  );

  return (
    <div className="modal-overlay">
      <motion.div 
        className="modal-content movie-search-modal"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
      >
        <div className="modal-header">
          <h2 className="modal-title pencil-text">
            {showDictionary ? "Результаты" : 
             showParameterSelection ? "Настройка словаря" : 
             showSubtitles ? "Субтитры" : 
             contentType === 'none' ? "Выберите тип контента" :
             contentType === 'series' ? "Поиск сериала" : "Поиск фильма"}
          </h2>
          <button className="modal-close-button" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="modal-body">
          {contentType === 'none' && renderContentTypeSelection()}
          
          {contentType === 'series' && !selectedMovie && !showSubtitles && !showParameterSelection && !showDictionary && renderSeriesForm()}
          
          {contentType === 'movie' && !selectedMovie && !showSubtitles && !showParameterSelection && !showDictionary && renderMovieForm()}

          {selectedMovie && !showSubtitles && !showParameterSelection && !showDictionary && (
            <div className="language-selection">
              <h3 className="results-title pencil-text">Выберите язык субтитров:</h3>
              <div className="language-selector">
                <select 
                  value={selectedMovie.selectedLanguage}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  className="language-select"
                >
                  <option value="ru">Русский</option>
                  <option value="en">English</option>
                  <option value="es">Español</option>
                  <option value="fr">Français</option>
                </select>
              </div>
            </div>
          )}

          {showSubtitles && selectedMovie && (
            <div className="subtitles-display">
              {isLoadingSubtitles ? (
                <div className="loading-subtitles">Загрузка субтитров...</div>
              ) : selectedMovie.subtitles ? (
                <div className="subtitles-content">
                  <pre>{selectedMovie.subtitles}</pre>
                </div>
              ) : error ? (
                <div className="subtitles-error">{error}</div>
              ) : null}
            </div>
          )}
          
          {showParameterSelection && renderParameterSelection()}
          
          {isGeneratingDictionary && (
            <div className="loading-dictionary">
              <div className="spinner"></div>
              <p>Создание словаря... Это может занять до 2 минут.</p>
            </div>
          )}
          
          {showDictionary && renderDictionary()}
        </div>

        <div className="modal-footer">
          <button 
            onClick={onClose} 
            className="cancel-button pencil-outline"
          >
            Отмена
          </button>
          <button 
            onClick={handleNextStep}
            disabled={(!selectedMovie && contentType !== 'none') || 
                     (showSubtitles && isLoadingSubtitles) ||
                     isGeneratingDictionary}
            className="next-button pencil-outline"
          >
            {showDictionary ? "Закрыть" : "Далее"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default MovieSearchModal; 